import React, { useState, useEffect, useRef } from "react";
import Swiper from "swiper";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import { RCL as useTranslation } from "../RCL";
import { Slider } from "../../sharedComponents/ComponentsSlider";
// eslint-disable-next-line import/no-cycle
import { Link } from "../../sharedComponents/Link";
import { AngleRight } from "../../sharedComponents/Images";
import { Icon } from "../Icons";
import * as styles from "./ComponentsSlider.module.scss";
import { WindowSizeDetector } from "../WindowSizeDetector";

const cx = classNames.bind(styles);

const getBreakpoints = (slidesPerPage, breakpoints) => {
	if (slidesPerPage && !breakpoints) {
		switch (slidesPerPage) {
			case 2:
				return {
					320: {
						slidesPerView: 1.1,
						spaceBetween: 12,
					},
					576: {
						slidesPerView: 2,
						spaceBetween: 12,
					},
					769: {
						slidesPerView: 2,
						spaceBetween: 16,
					},
				};
			case 3:
				return {
					320: {
						slidesPerView: 1.1,
						spaceBetween: 12,
					},
					576: {
						slidesPerView: 2,
						spaceBetween: 12,
					},
					769: {
						slidesPerView: 3,
						spaceBetween: 16,
					},
				};
			case 4:
				return {
					320: {
						slidesPerView: 1.1,
						spaceBetween: 12,
						centeredSlides: false,
					},
					576: {
						slidesPerView: 2,
						spaceBetween: 10,
						centeredSlides: false,
					},
					769: {
						slidesPerView: 4,
						spaceBetween: 16,
						centeredSlides: false,
					},
				};
			default:
				return null;
		}
	}
	return breakpoints;
};
const ComponentsSlider = ({
	slides,
	paging,
	navigation,
	slidesPerPage,
	breakpoints,
	link,
	linkIcon,
	variant,
	id,
	cardSlider,
}) => {
	const swiperRef = useRef(null);
	const [isSwiperReady, setIsSwiperReady] = useState(false);
	const paginationBulletMessage = useTranslation({
		searchKey: "Go-to-slide",
	})?.concat(" {{index}}");
	const breakpointInstance = getBreakpoints(slidesPerPage, breakpoints);
	let SliderInViewPort = slidesPerPage;

	const thisWindowSize = WindowSizeDetector();

	if (breakpointInstance !== null) {
		if (thisWindowSize > Number(Object.keys(breakpointInstance)[2])) {
			SliderInViewPort = breakpointInstance[Object.keys(breakpointInstance)[2]].slidesPerView;
		} else if (
			thisWindowSize > Number(Object.keys(getBreakpoints(slidesPerPage, breakpoints))[1])
		) {
			SliderInViewPort = breakpointInstance[Object.keys(breakpointInstance)[1]].slidesPerView;
		} else if (
			thisWindowSize > Number(Object.keys(getBreakpoints(slidesPerPage, breakpoints))[0])
		) {
			SliderInViewPort = breakpointInstance[Object.keys(breakpointInstance)[0]].slidesPerView;
		} else {
			SliderInViewPort = 1;
		}
	}

	useEffect(() => {
		swiperRef.current = new Swiper(".swiper-container", {
			loop: true,
			slidesPerView: "auto",
		});

		// Check if Swiper instance is available
		if (swiperRef.current) {
			setIsSwiperReady(true);
		}
	}, []);

	return (
		<>
			<Slider
				slides={slides}
				id={id}
				a11y={{
					prevSlideMessage: useTranslation({ searchKey: "previous-slide" }),
					nextSlideMessage: useTranslation({ searchKey: "next-slide" }),
					firstSlideMessage: "firstSlideLabel",
					lastSlideMessage: "lastSlideLabel",
					paginationBulletLabel: paginationBulletMessage,
				}}
				paginationClasses={{
					pagination: cx("pagination"),
					paginationItem: "paginationItem",
					bullet: cx("bullet"),
					bulletActive: cx("bulletActive"),
					bulletList: cx({
						paginationList: paging,
						hidePaginationList: slides.length <= Math.floor(SliderInViewPort),
						paginationListDesktopOnly: paging === "desktopOnly",
					}),
				}}
				navigationClasses={{
					button: cx({
						button: navigation,
						buttonDesktopOnly: navigation === "desktopOnly",
						hideButton: slides.length <= Math.floor(SliderInViewPort),
						buttonNoMargin: !cardSlider,
						wedding: variant === "wedding",
					}),
					prevButton: `prevButton-${id}`,
					nextButton: `nextButton-${id}`,
				}}
				paging={paging}
				// TODO: Should actually be true / false - but need to verify implementation in Slider
				leftNavigation={<Icon name="arrow-left" />}
				rightNavigation={<Icon name="arrow-right" />}
				slidesPerPage={slidesPerPage}
				SliderInViewPort={SliderInViewPort}
				breakpoints={getBreakpoints(slidesPerPage, breakpoints)}
				slideClass={cx({
					swiperSlide: slidesPerPage !== 1,
					swiperSlidePaged: slidesPerPage === 1,
				})}
				className={cx("sliderContainer")}
				moreLink={
					link && (
						<Link to={link.urlLink} className={cx("moreLink")} target={link.target}>
							{link.linkText}
							{linkIcon && <AngleRight className={cx("moreLinkImage")} />}
						</Link>
					)
				}
				PaginationCap={slides.length > 4 && thisWindowSize < 576}
				isSwiperReady={isSwiperReady}
			/>
		</>
	);
};

ComponentsSlider.propTypes = {
	slides: PropTypes.arrayOf(PropTypes.object).isRequired,
	paging: PropTypes.oneOf([true, false, "desktopOnly"]),
	navigation: PropTypes.oneOf([true, false, "desktopOnly"]),
	slidesPerPage: PropTypes.oneOf([1, 2, 3, 4]),
	breakpoints: PropTypes.object,
	link: PropTypes.shape({
		urlLink: PropTypes.string.isRequired,
		target: PropTypes.string,
		linkText: PropTypes.string,
	}),
	linkIcon: PropTypes.string,
	variant: PropTypes.oneOf(["primary", "wedding"]),
	id: PropTypes.string,
	cardSlider: PropTypes.bool,
};
ComponentsSlider.defaultProps = {
	paging: false,
	navigation: false,
	slidesPerPage: 3,
	breakpoints: null,
	link: null,
	linkIcon: null,
	id: null,
	variant: "primary",
	cardSlider: false,
};

export default ComponentsSlider;
export { ComponentsSlider };
